import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import ApiService from '../../../../services/ApiService';
import { format } from 'date-fns';

import { StockOutletType } from '..';
import { TypeMainOutlet } from '../..';
import { StockMovement } from './types';
import TableFiltersContext from '../../../../contexts/TableFiltersContext';
import SectionContext from '../../../../contexts/SectionContext';
import StockContext from '../../../../contexts/StockContext';
import ModalContext from '../../../../contexts/ModalContext';
import { useTenant } from '../../../../contexts/TenantContext';
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage';
import { buildMovementColumns, movementScreenKey } from './movementColumns';

const MovementTable: React.FC = () => {
  const { stockMode } = useContext(ModalContext);
  const { isFashionTenant } = useTenant();
  const isFashion = isFashionTenant || stockMode === 'fashion';
  const { refresh } = useContext(StockContext)
  const { movementFilters } = useContext(TableFiltersContext)
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const { movementTypeFilter } = useContext(TableFiltersContext);
  const { quantity, setQuantity } = useOutletContext<StockOutletType>()
  const { search } = useOutletContext<TypeMainOutlet>()
  const { setStockSection } = useContext(SectionContext)

  const columns = useMemo(() => buildMovementColumns(isFashion), [isFashion])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    movementScreenKey,
    columns,
  )

  useEffect(() => {
    setStockSection('movement');
    setQuantity(50);
  }, []);

  useEffect(() => {
    const parameters = {
      size: quantity,
      filters: movementFilters,
      global: search,
      movementType: movementTypeFilter
    };

    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await ApiService.movements.find(parameters)
        if (response?.data) {
          const items = Array.isArray(response.data) ? response.data : response.data.items || []
          setStockMovements(items)
        }
      } catch (error: any) {
        console.error(
          'Ocorreu um erro ao buscar os dados de movimento de estoque',
          error,
        )

        if (error.response?.status === 401) {
          localStorage.removeItem('token')
          window.location.href = '/'
        }
      } finally {
        setIsLoading(false)
      }
    }

    const timeoutId = setTimeout(fetchData, 300);
    return () => clearTimeout(timeoutId);
  }, [quantity, movementFilters, movementTypeFilter, search, refresh]);

  const matchesMovementType = (item: StockMovement) => {
    if (movementTypeFilter === "Manual") {
      return item.ploomesOrderId === 0 || item.ploomesOrderId === 1;
    }
    if (movementTypeFilter === "Pedido") {
      return item.ploomesOrderId !== 0 && item.ploomesOrderId !== 1;
    }
    return true;
  };

  const visibleMovements = stockMovements.filter(matchesMovementType)

  return (
    <StandardDataTable
      columns={columns}
      data={visibleMovements}
      columnsState={columnsState}
      onColumnsChange={handleColumnsChange}
      onRestoreColumns={handleRestoreColumns}
      totalCount={null}
      onLoadMore={() => setQuantity((prev) => prev + 50)}
      hasMore={stockMovements.length >= quantity}
      isLoading={isLoading}
      countLabel="movimentações"
      exportFileName="movimentacoes-estoque"
      rowClassName={(item) => (item.status === '410' ? 'yellowLine' : '')}
      rowTitle={(item) =>
        (item.UpdatedByUser?.name || item.CreatedByUser?.name) && (item.updatedAt || item.createdAt)
          ? `Atualizado por ${item.UpdatedByUser?.name || item.CreatedByUser?.name} em ${
            format(new Date(item.updatedAt || item.createdAt), 'dd/MM/yyyy - HH:mm')}h${
            item.observation ? `\nObservação: ${item.observation}` : ''
          }`
          : undefined
      }
    />
  );
};

export default MovementTable;
